import { useState } from "react";
import { toast } from "sonner";
import { openAuthFile, downloadAuthFile } from "../../lib/fileOpen";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Textarea } from "../ui/textarea";
import { DownloadSimple, SealCheck, SealWarning, Clock, GraduationCap, IdentificationCard, QrCode, ShareNetwork, LinkedinLogo, InstagramLogo, Copy } from "@phosphor-icons/react";

export const fmtD = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB") : "—");

/** Share: download a LinkedIn landscape or square card and copy a ready-to-paste caption with the verification link. */
function ShareDialog({ c }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [caption, setCaption] = useState(c.caption || "");
  const grab = (shape) => {
    setBusy(shape);
    downloadAuthFile(`${c.share_url}?shape=${shape}`, `${c.reference || "certificate"}_${shape}.png`).finally(() => setBusy(""));
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(caption); toast.success("Caption copied — paste it into your post"); }
    catch { toast.error("Couldn't copy — select the text and copy it instead"); }
  };
  return (
    <>
      <Button size="sm" variant="outline" className="h-8 text-xs bg-background" onClick={() => setOpen(true)} data-testid={`cert-share-${c.id}`}><ShareNetwork size={13} className="mr-1" /> Share</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid={`cert-share-dialog-${c.id}`}>
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Share your certificate</DialogTitle>
            <DialogDescription>Download a share card, then paste the caption into your post — it links to the public verification page.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" className="h-auto py-3 flex-col gap-1" disabled={!!busy} onClick={() => grab("landscape")} data-testid={`cert-share-landscape-${c.id}`}>
              <LinkedinLogo size={20} /><span className="text-xs font-medium">{busy === "landscape" ? "Preparing…" : "Landscape · LinkedIn"}</span><span className="text-[10px] text-muted-foreground">1200 × 627</span>
            </Button>
            <Button variant="outline" className="h-auto py-3 flex-col gap-1" disabled={!!busy} onClick={() => grab("square")} data-testid={`cert-share-square-${c.id}`}>
              <InstagramLogo size={20} /><span className="text-xs font-medium">{busy === "square" ? "Preparing…" : "Square · Instagram / WhatsApp"}</span><span className="text-[10px] text-muted-foreground">1080 × 1080</span>
            </Button>
          </div>
          <div>
            <p className="text-xs font-medium mb-1">Post caption</p>
            <Textarea rows={6} value={caption} onChange={(e) => setCaption(e.target.value)} className="text-xs" data-testid={`cert-share-caption-${c.id}`} />
            <div className="flex justify-end mt-2"><Button size="sm" onClick={copy} data-testid={`cert-share-copy-${c.id}`}><Copy size={13} className="mr-1" /> Copy caption</Button></div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

const TONE = {
  valid: { ring: "stroke-emerald-500", text: "text-emerald-700 dark:text-emerald-300", bg: "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900", label: "In date" },
  expiring: { ring: "stroke-amber-500", text: "text-amber-700 dark:text-amber-300", bg: "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900", label: "Expiring soon" },
  expired: { ring: "stroke-red-500", text: "text-red-700 dark:text-red-300", bg: "bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900", label: "Expired" },
};

/** Countdown ring: how much of the certificate's life is left (full ring = freshly issued). */
export function Countdown({ c }) {
  const t = TONE[c.status] || TONE.valid;
  if (c.days_left == null) return <div className="flex flex-col items-center justify-center w-20 h-20 rounded-full border-2 border-dashed border-border text-[10px] text-muted-foreground text-center leading-tight" data-testid={`cert-countdown-${c.id}`}>No<br />expiry</div>;
  const total = c.issued_at && c.expires_at ? Math.max(1, (new Date(c.expires_at) - new Date(c.issued_at)) / 86400000) : 365;
  const frac = Math.max(0, Math.min(1, c.days_left / total));
  const r = 34, circ = 2 * Math.PI * r;
  const big = c.days_left < 0 ? Math.abs(c.days_left) : c.days_left;
  return (
    <div className="relative w-20 h-20 shrink-0" data-testid={`cert-countdown-${c.id}`} title={c.days_left < 0 ? `Expired ${big} days ago` : `${big} days left`}>
      <svg viewBox="0 0 80 80" className="w-20 h-20 -rotate-90">
        <circle cx="40" cy="40" r={r} className="stroke-border" strokeWidth="6" fill="none" />
        <circle cx="40" cy="40" r={r} className={`${t.ring} transition-[stroke-dashoffset] duration-700`} strokeWidth="6" fill="none" strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={circ * (1 - (c.days_left < 0 ? 0 : frac))} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
        <span className={`text-lg font-bold font-[Chivo] ${t.text}`}>{big}</span>
        <span className="text-[9px] text-muted-foreground">{c.days_left < 0 ? "days ago" : "days left"}</span>
      </div>
    </div>
  );
}

/** One certificate: countdown ring, status, download / share / verify — `actions` adds office controls (e.g. Remind). */
export function CertCard({ c, actions = null, share = true }) {
  const t = TONE[c.status] || TONE.valid;
  const Icon = c.source === "training" ? GraduationCap : IdentificationCard;
  return (
    <div className={`rounded-xl border p-4 flex gap-4 ${t.bg}`} data-testid={`cert-card-${c.id}`}>
      <Countdown c={c} />
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1"><Icon size={11} /> {c.source === "training" ? "Training certificate" : "Card / ticket"}</p>
        <h3 className="font-semibold text-sm leading-tight mt-0.5" data-testid={`cert-title-${c.id}`}>{c.type_name}</h3>
        {c.course_title && c.course_title !== c.type_name && <p className="text-xs text-muted-foreground">{c.course_title}</p>}
        <p className="text-xs text-muted-foreground mt-1">{c.reference && <span className="font-mono">{c.reference} · </span>}Issued {fmtD(c.issued_at)} · {c.expires_at ? `Expires ${fmtD(c.expires_at)}` : "No expiry"}{c.score != null ? ` · ${c.score}%` : ""}</p>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${t.text}`} data-testid={`cert-status-${c.id}`}>{c.status === "valid" ? <SealCheck size={13} weight="fill" /> : c.status === "expiring" ? <Clock size={13} weight="fill" /> : <SealWarning size={13} weight="fill" />} {t.label}</span>
          {c.download_url ? <Button size="sm" variant="outline" className="h-8 text-xs bg-background" onClick={() => openAuthFile(c.download_url).catch(() => toast.error("Couldn't open the certificate"))} data-testid={`cert-download-${c.id}`}><DownloadSimple size={13} className="mr-1" /> Download</Button>
            : <span className="text-[11px] text-muted-foreground" data-testid={`cert-nofile-${c.id}`}>No file on record — ask the office to upload it</span>}
          {share && c.share_url && c.status !== "expired" && <ShareDialog c={c} />}
          {actions}
          {c.verify_url && <a href={c.verify_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline" data-testid={`cert-verify-${c.id}`}><QrCode size={12} /> Verify</a>}
        </div>
        {c.status !== "valid" && <p className="text-[11px] text-muted-foreground mt-1.5" data-testid={`cert-renew-${c.id}`}>{c.source === "training" ? "Ask your manager to re-assign the course so you can renew it." : "Book a renewal and send the new card to the office."}</p>}
      </div>
    </div>
  );
}

